import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Mic2, Sparkles, Wand2, X } from "lucide-react";

const STORAGE_KEY = "voxara-landing-speakers-announcement-dismissed";

const copyByLanguage = {
  es: {
    liveLabel: "Novedad LIVE",
    title: "Voxara ya identifica hablantes con IA",
    description: "Sube tu audio o video, deja que la IA detecte hablantes y luego ajusta nombres y segmentos desde el timeline antes de exportar el reporte.",
    bullets: [
      "Identificación automática de hablantes con IA",
      "Edición visual en timeline, por rangos y segmentos",
      "Mejores reportes PDF y Word con speakers revisados",
    ],
    primaryCta: "Prueba gratis",
    secondaryCta: "Seguir explorando",
  },
  en: {
    liveLabel: "LIVE update",
    title: "Voxara now identifies speakers with AI",
    description: "Upload your audio or video, let AI detect speakers, then fine-tune names and ranges from the timeline before exporting your report.",
    bullets: [
      "Automatic AI speaker identification",
      "Visual timeline editing by range and segment",
      "Cleaner PDF and Word reports after review",
    ],
    primaryCta: "Try free",
    secondaryCta: "Keep exploring",
  },
};

export const LandingSpeakersAnnouncement = ({ language = "es" }) => {
  const [isVisible, setIsVisible] = useState(false);
  const copy = useMemo(() => copyByLanguage[language] || copyByLanguage.es, [language]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const dismissed = window.sessionStorage.getItem(STORAGE_KEY) === "1";
    if (dismissed) return undefined;

    const timer = window.setTimeout(() => setIsVisible(true), 240);
    return () => window.clearTimeout(timer);
  }, []);

  const closeAnnouncement = () => {
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem(STORAGE_KEY, "1");
    }
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-start justify-center overflow-y-auto px-4 py-20 sm:px-6" data-testid="landing-speakers-announcement-overlay">
      <button
        type="button"
        aria-label={language === "es" ? "Cerrar anuncio" : "Close announcement"}
        className="absolute inset-0 bg-black/68 backdrop-blur-[3px]"
        onClick={closeAnnouncement}
        data-testid="landing-speakers-announcement-backdrop"
      />

      <div className="relative z-[1] w-full max-w-2xl animate-fade-in" data-testid="landing-speakers-announcement-panel">
        <div className="relative overflow-hidden rounded-[30px] border border-primary/25 bg-[#090910]/95 p-6 shadow-[0_30px_90px_rgba(0,0,0,0.55)] sm:p-8">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(248,0,248,0.22),transparent_42%),radial-gradient(circle_at_bottom_left,rgba(255,255,255,0.08),transparent_36%)]" />

          <div className="relative flex items-start justify-between gap-4">
            <div className="space-y-4">
              <Badge className="rounded-full border border-primary/25 bg-primary/12 px-3 py-1 text-[11px] uppercase tracking-[0.22em] text-primary" data-testid="landing-speakers-announcement-badge">
                {copy.liveLabel}
              </Badge>

              <div className="space-y-3">
                <div className="flex items-center gap-3 text-primary">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 shadow-[0_0_24px_rgba(248,0,248,0.18)]">
                    <Mic2 className="h-6 w-6" />
                  </div>
                  <Sparkles className="h-5 w-5" />
                </div>

                <h2 className="max-w-[18ch] text-3xl font-bold leading-[1.02] tracking-tight text-white sm:text-4xl" data-testid="landing-speakers-announcement-title">
                  {copy.title}
                </h2>
                <p className="max-w-2xl text-sm leading-7 text-white/72 sm:text-base" data-testid="landing-speakers-announcement-description">
                  {copy.description}
                </p>
              </div>
            </div>

            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="relative z-[1] h-10 w-10 rounded-full border border-white/10 bg-white/5 text-white/75 hover:bg-white/10 hover:text-white"
              onClick={closeAnnouncement}
              data-testid="landing-speakers-announcement-close-button"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="relative mt-6 grid gap-3" data-testid="landing-speakers-announcement-bullets">
            {copy.bullets.map((bullet, index) => (
              <div
                key={bullet}
                className="flex items-start gap-3 rounded-2xl border border-white/8 bg-white/5 px-4 py-3 text-left"
                style={{ animationDelay: `${index * 0.08}s` }}
                data-testid={`landing-speakers-announcement-bullet-${index + 1}`}
              >
                <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-primary/20 bg-primary/10 text-primary">
                  <Wand2 className="h-3.5 w-3.5" />
                </div>
                <p className="text-sm leading-6 text-white/82 sm:text-[0.98rem]">{bullet}</p>
              </div>
            ))}
          </div>

          <div className="relative mt-7 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <Link to="/signup" className="w-full sm:w-auto" onClick={closeAnnouncement}>
              <Button className="speaker-edit-cta h-12 w-full rounded-2xl px-6 text-sm font-semibold sm:w-auto" data-testid="landing-speakers-announcement-primary-button">
                <Sparkles className="mr-2 h-4 w-4" />
                {copy.primaryCta}
              </Button>
            </Link>

            <Button
              type="button"
              variant="outline"
              onClick={closeAnnouncement}
              className="h-12 rounded-2xl border-white/12 bg-white/5 px-6 text-sm text-white hover:bg-white/10"
              data-testid="landing-speakers-announcement-secondary-button"
            >
              {copy.secondaryCta}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingSpeakersAnnouncement;
